import { excelSerialToJsDate, javascriptDateToMMDDYYYY, javascriptDateToYYYYMMDD } from '../helpers/date-helpers';
import { isSupportedDataType, isValidCurrency, isValidDateInput, isValidExchangeTickerSymbol } from '../helpers/args-validators';
import { FunctionCallCache } from '../helpers/functionCallCache';
import { FunctionLocationTracker } from '../helpers/functionLocationTracker';
import { KeyBuilder } from '../helpers/keyBuilder';
const SUPPORTED_SOURCES = ['EODHD'];
const SUPPORTED_DATETYPES = ['Market_Cap'];
/**
 * Validates arguments for Capitalization Summary.
 */
function validateArgs(symbol, source, dataType, currency, date) {
    if (!isValidExchangeTickerSymbol(symbol)) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Invalid symbol (expected format: EXCHANGE:TICKER)');
    }
    if (!source || !SUPPORTED_SOURCES.includes(source.toUpperCase())) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Invalid source (supported: EODHD)');
    }
    if (!isSupportedDataType(SUPPORTED_DATETYPES, dataType)) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Invalid data type (supported: Market_Cap)');
    }
    if (!isValidCurrency(currency)) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, "Invalid currency (expected format: e.g., 'USD')");
    }
    if (!isValidDateInput(String(date))) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Invalid date');
    }
    return true;
}
let delegate = () => {
    throw new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable, 'Initializing… press F9 to recalc.');
};
export function setCapitalizationSummaryDelegate(fn) {
    delegate = fn;
}
/**
 * Wrapper that calls the injected delegate (pattern used throughout excel-core).
 */
export function capitalizationSummaryWrapper(symbol, source, dataType, currency, date, invocation) {
    return delegate(symbol, source, dataType, currency, date, invocation);
}
/**
 * Actual implementation that registers a cached API function call.
 */
export function capitalizationSummaryImpl(symbol, source, dataType, currency, date, invocation) {
    validateArgs(symbol, source, dataType, currency, date);
    const javascriptDate = String(date).includes('/') ? new Date(date) : excelSerialToJsDate(Number(date));
    const queryDate = javascriptDateToYYYYMMDD(javascriptDate);
    // ✅ Normalized args to match backend expectations
    // Backend expects:
    // { symbol, source: 'EODHD', dataType: 'market_cap', currency, date: 'YYYY-MM-DD' }
    const normalizedSource = source.toUpperCase();
    const normalizedDataType = dataType.toLowerCase(); // "Market_Cap" -> "market_cap" (validators already match supported list)
    const args = {
        symbol,
        source: normalizedSource,
        dataType: normalizedDataType,
        currency: currency.toUpperCase(),
        date: queryDate,
    };
    const cache = FunctionCallCache.getInstance();
    const tracker = FunctionLocationTracker.getInstance();
    const key = KeyBuilder.build('capitalization_summary', args);
    cache.registerByKey(key, { functionName: 'capitalization_summary', params: args });
    if (invocation?.address) {
        tracker.registerCell(key, invocation.address, `=ALPHA.CAPITALIZATION_SUMMARY("${symbol}", "${normalizedSource}", "${dataType}", "${currency.toUpperCase()}", "${javascriptDateToMMDDYYYY(javascriptDate)}")`);
        cache.setAutoRefresh(key);
    }
    return cache.getValueForExcelByKey(key);
}
//# sourceMappingURL=capitalizationSummary.js.map